'use client';

import { useEffect } from 'react';

const GLITCHREPLAY_DSN =
  'https://3f96a35586c8084a81d210d137c318d6@glitchreplay.com/prj_0c53e164928b0a1f7bd2';

/**
 * Next.js segment error boundary for /tools/*. Catches React render-time
 * errors and uncaught throws inside any tool page — these don't reach
 * window.onerror, so the GlobalErrorHandler in the root layout misses
 * them. This boundary closes that gap without per-tool wiring.
 *
 * Tagged surface=tool_error_boundary in the GlitchReplay envelope so we
 * can distinguish render-time crashes from window-level uncaught errors.
 */
export default function ToolError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportToGlitchReplay({
      message: error.message || 'Tool render error',
      stackTrace: error.stack,
      pageUrl: typeof window !== 'undefined' ? window.location.pathname : '/tools',
      digest: error.digest,
    });
  }, [error]);

  return (
    <div className="mx-auto max-w-2xl px-6 py-16 text-center">
      <h2 className="text-2xl font-bold text-gray-900">Something went wrong with this tool</h2>
      <p className="mt-3 text-gray-600">
        We&apos;ve been notified. Try again, or browse other tools.
      </p>
      {error.digest && (
        <p className="mt-2 text-xs text-gray-400">Reference: {error.digest}</p>
      )}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <button
          onClick={reset}
          className="rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700"
        >
          Try again
        </button>
        <a
          href="/tools"
          className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 font-medium text-gray-900 hover:bg-gray-50"
        >
          Browse other tools
        </a>
      </div>
    </div>
  );
}

function reportToGlitchReplay(p: {
  message: string;
  stackTrace?: string;
  pageUrl: string;
  digest?: string;
}) {
  try {
    const m = GLITCHREPLAY_DSN.match(/^https:\/\/([^@]+)@([^/]+)\/(.+)$/);
    if (!m) return;
    const [, key, host, pid] = m;
    const eventId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID().replace(/-/g, '')
        : Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    const frames = (p.stackTrace ?? '')
      .split('\n')
      .slice(1, 16)
      .map((line) => {
        const f = line.trim().match(/at\s+(?:(.+?)\s+\()?([^\s()]+):(\d+):(\d+)/);
        return f
          ? {
              function: f[1] || '<anonymous>',
              filename: f[2],
              lineno: Number(f[3]),
              colno: Number(f[4]),
              in_app: !f[2].includes('node_modules'),
            }
          : { function: line.trim() || '<anonymous>', in_app: false };
      });
    const env = [
      JSON.stringify({ event_id: eventId, sent_at: new Date().toISOString() }),
      JSON.stringify({ type: 'event' }),
      JSON.stringify({
        event_id: eventId,
        timestamp: Math.floor(Date.now() / 1000),
        platform: 'javascript',
        environment: process.env.NODE_ENV || 'production',
        tags: { surface: 'tool_error_boundary', digest: p.digest ?? '' },
        exception: {
          values: [
            {
              type: 'js_error',
              value: p.message,
              stacktrace: frames.length ? { frames } : undefined,
            },
          ],
        },
        request: { url: p.pageUrl, headers: { 'User-Agent': navigator.userAgent } },
      }),
    ].join('\n');
    const url = `https://${host}/api/${pid}/envelope/?sentry_key=${key}`;
    if (navigator.sendBeacon) {
      const blob = new Blob([env], { type: 'text/plain' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: env,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never throw */
  }
}
